import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, StatusPill, DatePicker, Modal } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { ErrorMessage, LineItemDetailSkeleton } from '@/components/common';
import { AuditViewer } from '@/components/audit';
import { CommentInput, PaginatedCommentHistory } from '@/components/comments';
import { useCreateLineItemComment, useSearchLineItemComments } from '@/hooks/useComments';
import { useAuthContext } from '@/hooks/useAuthContext';
import { websocketClient } from '@/services/websocket-client';
import { get } from '@/lib/api-client';
import { useAssignToSelf, useAssignToUser, useChartingTeamMembers, useUpdateHoldPosition } from '@/hooks/useCharting';
import { useUpdateLineItem } from '@/hooks/useHoldRequests';
import { useResubmitException } from '@/hooks/useExceptions';
import { useShowingTypes } from '@/hooks/useShowingTypes';
import { HoldPositionDropdown } from '@/components/charting/HoldPositionDropdown';
import { HoldPositionBadge } from '@/components/charting/HoldPositionBadge';
import { differenceInBusinessDays } from 'date-fns';
import type { InventoryLineItem, HoldRequest, ActionContext, CreateLineItemCommentData, User, HoldPosition } from '@/types';

/**
 * Converts an ISO datetime string (e.g., "2026-05-15T00:00:00.000Z") to M/d/yyyy format.
 * Returns empty string if input is falsy or unparseable.
 */
function isoToDisplayDate(isoStr: string | undefined): string {
  if (!isoStr) return '';
  const dateOnly = isoStr.split('T')[0];
  if (!dateOnly) return '';
  const match = dateOnly.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const [, y, m, d] = match;
    return `${Number(m)}/${Number(d)}/${y}`;
  }
  return dateOnly;
}

/**
 * Normalizes a date string (M/d/yyyy or yyyy-MM-dd) to a comparable yyyy-MM-dd key.
 * Used for change detection so both user-typed formats compare correctly.
 */
function normalizeDateKey(val: string): string {
  if (!val) return '';
  // Already yyyy-MM-dd
  const isoMatch = val.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) return val;
  // M/d/yyyy
  const slashMatch = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, m, d, y] = slashMatch;
    return `${y}-${m!.padStart(2, '0')}-${d!.padStart(2, '0')}`;
  }
  return val;
}

interface LineItemDetailProps {
lineItemId: string;
onClose: () => void;
onAssignToSelf?: (lineItemId: string) => void;
onMarkCompleted?: (lineItemId: string, comments?: string) => void;
onRequireException?: (lineItemId: string, reason: string) => void;
onNavigateToLineItem?: (lineItemId: string) => void; // For modal navigation
canAssign?: boolean;
canComplete?: boolean;
isAssignedToUser?: boolean;
isModal?: boolean; // Whether this is rendered as a modal or a full page
}

interface LineItemDetailData {
lineItem: InventoryLineItem;
holdRequest: HoldRequest;
}

export function LineItemDetail({
lineItemId,
onClose,
onAssignToSelf: _onAssignToSelf,
onMarkCompleted,
onRequireException,
onNavigateToLineItem,
canAssign: _canAssign = false,
canComplete = false,
isAssignedToUser = false,
isModal = true,
}: LineItemDetailProps) {
const [showAuditTrail, setShowAuditTrail] = useState(false);
const [showComments, setShowComments] = useState(true);
const [showActionModal, setShowActionModal] = useState(false);
const [linkCopied, setLinkCopied] = useState(false);
const [actionType, setActionType] = useState<'complete' | 'exception' | null>(null);
const [comments, setComments] = useState('');
const [completionHoldPosition, setCompletionHoldPosition] = useState<string>('');
const [showAssignModal, setShowAssignModal] = useState(false);
const [selectedUserId, setSelectedUserId] = useState<string>('');
const [isEditingDates, setIsEditingDates] = useState(false);
const [editedDates, setEditedDates] = useState({
flightStartDate: '',
flightEndDate: '',
holdExpirationDate: '',
});
const [editComment, setEditComment] = useState('');
const [dateValidationError, setDateValidationError] = useState<string | null>(null);
const [isEditingRejected, setIsEditingRejected] = useState(false);
const [rejectedEditData, setRejectedEditData] = useState<Record<string, string>>({});
const [isEditingInventory, setIsEditingInventory] = useState(false);
const [editedInventory, setEditedInventory] = useState({
  qty: 1,
  digitalOnlyNoOfSpots: '' as string | number,
});
const [inventoryEditComment, setInventoryEditComment] = useState('');
const [inventoryValidationError, setInventoryValidationError] = useState<string | null>(null);
const { user } = useAuthContext();
const navigate = useNavigate();
const queryClient = useQueryClient();
const createComment = useCreateLineItemComment();
const assignToSelf = useAssignToSelf();
const assignToUser = useAssignToUser();
const updateLineItem = useUpdateLineItem();
const updateHoldPosition = useUpdateHoldPosition();
const resubmitException = useResubmitException();
const { data: teamMembers } = useChartingTeamMembers();
const { data: showingTypes = [] } = useShowingTypes();

// Fetch exception comments for rejected items
const { data: exceptionCommentsData } = useSearchLineItemComments(
lineItemId,
{ actionContext: 'exception' },
{ limit: 10, sortOrder: 'desc' }
);

// Get the most recent exception comment (from admin/exceptions team)
const latestExceptionComment = exceptionCommentsData?.comments?.[0];

const { data, isLoading, error } = useQuery({
queryKey: ['lineItemDetail', lineItemId],
queryFn: async (): Promise<LineItemDetailData> => {
try {
const response = await get<{
success: boolean;
data: { lineItem: InventoryLineItem };
}>(`/items/${lineItemId}`);

if (!response.success || !response.data || !response.data.lineItem) {
throw new Error('Line item not found in response');
}

const lineItem = response.data.lineItem;

if (!lineItem.holdRequestId) {
throw new Error('Line item missing holdRequestId');
}

// Fetch the parent hold request for additional context
const holdResponse = await get<{
success: boolean;
data: { holdRequest: HoldRequest };
}>(`/holds/${lineItem.holdRequestId}`);

if (!holdResponse.success || !holdResponse.data || !holdResponse.data.holdRequest) {
throw new Error('Hold request not found in response');
}

const holdRequest = holdResponse.data.holdRequest;

return { lineItem, holdRequest };
} catch (error) {
console.error('Error fetching line item details:', error);
throw error;
}
},
});

// Fetch all line items for this hold request to enable navigation
const { data: allLineItems } = useQuery({
queryKey: ['holdRequestLineItems', data?.lineItem.holdRequestId],
queryFn: async () => {
if (!data?.lineItem.holdRequestId) return [];

const response = await get<{
success: boolean;
data: { lineItems: InventoryLineItem[] };
}>(`/holds/${data.lineItem.holdRequestId}/items`);

if (!response.success || !response.data) {
return [];
}

return response.data.lineItems || [];
},
enabled: !!data?.lineItem.holdRequestId,
});

// Calculate current index and navigation
const currentIndex = allLineItems?.findIndex(item => item.id === lineItemId) ?? -1;
const totalLineItems = allLineItems?.length ?? 0;
const hasPrevious = totalLineItems > 1;
const hasNext = totalLineItems > 1;

// Navigate to previous line item (with wrap-around)
const handlePrevious = () => {
if (!allLineItems || allLineItems.length <= 1) return;

const prevIndex = currentIndex <= 0 ? allLineItems.length - 1 : currentIndex - 1;
const prevLineItem = allLineItems[prevIndex];

if (!prevLineItem) return;

if (isModal && onNavigateToLineItem) {
// In modal mode, use callback to avoid URL navigation
onNavigateToLineItem(prevLineItem.id);
} else {
// In full page mode, navigate normally
navigate(`/items/${prevLineItem.id}`);
}
};

// Navigate to next line item (with wrap-around)
const handleNext = () => {
if (!allLineItems || allLineItems.length <= 1) return;

const nextIndex = currentIndex >= allLineItems.length - 1 ? 0 : currentIndex + 1;
const nextLineItem = allLineItems[nextIndex];

if (!nextLineItem) return;

if (isModal && onNavigateToLineItem) {
// In modal mode, use callback to avoid URL navigation
onNavigateToLineItem(nextLineItem.id);
} else {
// In full page mode, navigate normally
navigate(`/items/${nextLineItem.id}`);
}
};

// Copy direct link to line item
const handleCopyLink = async () => {
const url = `${window.location.origin}/items/${lineItemId}`;
try {
await navigator.clipboard.writeText(url);
setLinkCopied(true);
setTimeout(() => setLinkCopied(false), 2000);
} catch {
// Fallback for older browsers
const textarea = document.createElement('textarea');
textarea.value = url;
textarea.style.position = 'fixed';
textarea.style.opacity = '0';
document.body.appendChild(textarea);
textarea.select();
document.execCommand('copy');
document.body.removeChild(textarea);
setLinkCopied(true);
setTimeout(() => setLinkCopied(false), 2000);
}
};

// Keyboard navigation
useEffect(() => {
const handleKeyDown = (e: KeyboardEvent) => {
// Only handle arrow keys if not in an input/textarea
if (
e.target instanceof HTMLInputElement ||
e.target instanceof HTMLTextAreaElement ||
e.target instanceof HTMLSelectElement
) {
return;
}

if (e.key === 'ArrowLeft' && hasPrevious) {
e.preventDefault();
handlePrevious();
} else if (e.key === 'ArrowRight' && hasNext) {
e.preventDefault();
handleNext();
}
};

window.addEventListener('keydown', handleKeyDown);
return () => window.removeEventListener('keydown', handleKeyDown);
}, [hasPrevious, hasNext, currentIndex, allLineItems]);


// Subscribe to real-time comment updates via WebSocket
useEffect(() => {
if (!lineItemId) return;

// Subscribe to line item updates
websocketClient.subscribeToLineItem(lineItemId);

// Listen for comment events
const handleCommentUpdate = () => {
// Invalidate comment queries to refetch
queryClient.invalidateQueries({
queryKey: ['lineItemComments', lineItemId],
});
};

// Set up listener for line item updates (which includes comment events)
const unsubscribe = websocketClient.onStatusUpdate(
`line_item:${lineItemId}`,
handleCommentUpdate
);

// Cleanup
return () => {
unsubscribe();
websocketClient.unsubscribeFromLineItem(lineItemId);
};
}, [lineItemId, queryClient]);

// Check if user can edit line items
const canEditLineItem = useCallback(() => {
if (!user || !data) return false;

const userRole = user.role;
const { lineItem } = data;

// If line item has an active exception (in_review), only exceptions team and leadership can edit
const hasActiveException = lineItem.exceptionStatus === 'in_review';

// Charting members and managers can edit standard holds only (not items with active exceptions)
if (userRole === 'charting_member' || userRole === 'charting_manager') {
return !hasActiveException;
}

// Leadership roles can always edit
if (
userRole === 'sales_vp' ||
userRole === 'gvp' ||
userRole === 'rvp' ||
userRole === 'adops_leadership'
) {
return true;
}

return false;
}, [user, data]);

// Check if user can edit hold position on this line item
// Property 3: Editable iff (a) charting_member/charting_manager + status open/in_progress,
// (b) charting_member assigned or charting_manager + status completed, (c) leadership role
// Note: Items with active exceptions (in_review) cannot be edited by charting team
const canEditHoldPosition = useCallback(() => {
if (!user || !data) return false;

const { lineItem } = data;
const userRole = user.role;
const status = lineItem.chartingStatus;

// If line item has an active exception, charting team cannot edit
const hasActiveException = lineItem.exceptionStatus === 'in_review';

// (c) Leadership roles can always edit
if (
userRole === 'sales_vp' ||
userRole === 'gvp' ||
userRole === 'rvp' ||
userRole === 'adops_leadership'
) {
return true;
}

// Charting team cannot edit items with active exceptions
if (hasActiveException) {
return false;
}

// (a) Charting member or manager + status open or in_progress
if (
(userRole === 'charting_member' || userRole === 'charting_manager') &&
(status === 'open' || status === 'in_progress')
) {
return true;
}

// (b) Charting member assigned to the line item OR charting manager + status completed
if (status === 'completed') {
if (userRole === 'charting_manager') {
return true;
}
if (userRole === 'charting_member' && lineItem.assignedChartingUserId === user.id) {
return true;
}
}

return false;
}, [user, data]);

// Initialize edit dates when entering edit mode
const handleStartEditDates = () => {
if (!data?.lineItem) return;

setEditedDates({
flightStartDate: isoToDisplayDate(data.lineItem.flightStartDate),
flightEndDate: isoToDisplayDate(data.lineItem.flightEndDate),
holdExpirationDate: isoToDisplayDate(data.lineItem.holdExpirationDate),
});
setEditComment('');
setDateValidationError(null);
setIsEditingDates(true);
};

// Validate dates when they change
useEffect(() => {
if (!isEditingDates || !data?.lineItem) return;

const { flightStartDate, flightEndDate } = editedDates;
const holdExpirationDate = editedDates.holdExpirationDate;

if (!flightStartDate || !flightEndDate) {
setDateValidationError(null);
return;
}

const flightStart = new Date(flightStartDate);
const flightEnd = new Date(flightEndDate);

// Check hold expiration vs flight start order
if (holdExpirationDate) {
const holdExp = new Date(holdExpirationDate);
if (holdExp >= flightStart) {
setDateValidationError('Hold expiration must be before flight start');
return;
}

// Check Movie Studio gap requirement
if (data.holdRequest.businessCategory === 'movie_studio') {
const gap = differenceInBusinessDays(flightStart, holdExp);
if (gap < 60) {
setDateValidationError(`Movie Studio requires 60 business-day gap. Current: ${gap} days`);
return;
}
}
}

if (flightStart >= flightEnd) {
setDateValidationError('Flight start must be before flight end');
return;
}

setDateValidationError(null);
}, [editedDates, isEditingDates, data]);

// Validate inventory edits
useEffect(() => {
if (!isEditingInventory) return;
const qty = Number(editedInventory.qty);
if (!Number.isInteger(qty) || qty < 1) {
  setInventoryValidationError('Qty must be an integer >= 1');
  return;
}
if (editedInventory.digitalOnlyNoOfSpots !== '') {
  const spots = Number(editedInventory.digitalOnlyNoOfSpots);
  if (!Number.isInteger(spots) || spots < 0) {
    setInventoryValidationError('# of Spots must be an integer >= 0');
    return;
  }
}
setInventoryValidationError(null);
}, [editedInventory, isEditingInventory]);

// Handle save dates
const handleSaveDates = async () => {
if (!data?.lineItem || dateValidationError) return;

// Normalize date string to ISO datetime format (handles both yyyy-MM-dd and M/d/yyyy from DatePicker)
// Uses date-fns parse to avoid timezone shift from native Date constructor
const toISODatetime = (dateStr: string): string => {
// Try yyyy-MM-dd first (pre-populated format)
const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
if (isoMatch) {
return `${dateStr}T00:00:00.000Z`;
}
// Try M/d/yyyy (DatePicker calendar selection format)
const parts = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
if (parts) {
const mm = parts[1]!.padStart(2, '0');
const dd = parts[2]!.padStart(2, '0');
const yyyy = parts[3]!;
return `${yyyy}-${mm}-${dd}T00:00:00.000Z`;
}
// Fallback
return `${dateStr}T00:00:00.000Z`;
};

try {
// Convert date strings to ISO datetime format (add time component)
const updateData: Record<string, string | undefined> = {
flightStartDate: editedDates.flightStartDate ? toISODatetime(editedDates.flightStartDate) : undefined,
flightEndDate: editedDates.flightEndDate ? toISODatetime(editedDates.flightEndDate) : undefined,
};

if (editedDates.holdExpirationDate) {
updateData.holdExpirationDate = toISODatetime(editedDates.holdExpirationDate);
}

await updateLineItem.mutateAsync({
id: lineItemId,
data: updateData,
});

// Add comment if provided and any date changed
if (editComment.trim()) {
const originalLineItem = data.lineItem;
// Normalize both sides through a date parser so M/d/yyyy and yyyy-MM-dd compare correctly
const flightDatesChanged = 
normalizeDateKey(editedDates.flightStartDate) !== normalizeDateKey(isoToDisplayDate(originalLineItem.flightStartDate)) ||
normalizeDateKey(editedDates.flightEndDate) !== normalizeDateKey(isoToDisplayDate(originalLineItem.flightEndDate));
const holdExpChanged =
normalizeDateKey(editedDates.holdExpirationDate) !== normalizeDateKey(isoToDisplayDate(originalLineItem.holdExpirationDate));

if (flightDatesChanged || holdExpChanged) {
const actionContext: ActionContext = flightDatesChanged ? 'flight_date_update' : 'expiration_update';
await handleCommentSubmit(editComment, actionContext);
}
}

// Refresh data
queryClient.invalidateQueries({ queryKey: ['lineItemDetail', lineItemId] });

setIsEditingDates(false);
setEditComment('');
} catch (error) {
console.error('Failed to update dates:', error);
alert('Failed to update dates. Please try again.');
}
};

// Handle cancel edit
const handleCancelEditDates = () => {
setIsEditingDates(false);
setEditComment('');
setDateValidationError(null);
};

// Inventory editing handlers
const handleStartEditInventory = () => {
if (!data?.lineItem) return;
setEditedInventory({
  qty: data.lineItem.qty ?? 1,
  digitalOnlyNoOfSpots: data.lineItem.digitalOnlyNoOfSpots ?? '',
});
setInventoryEditComment('');
setInventoryValidationError(null);
setIsEditingInventory(true);
};

const handleSaveInventory = async () => {
if (!data?.lineItem || inventoryValidationError) return;

const updateData: Record<string, number | string | undefined> = {};
const qtyVal = Number(editedInventory.qty);
const spotsVal = editedInventory.digitalOnlyNoOfSpots === ''
  ? undefined
  : Number(editedInventory.digitalOnlyNoOfSpots);

if (qtyVal !== (data.lineItem.qty ?? 1)) updateData.qty = qtyVal;
if (spotsVal !== data.lineItem.digitalOnlyNoOfSpots) updateData.digitalOnlyNoOfSpots = spotsVal;

// Only call API if something changed
if (Object.keys(updateData).length === 0) {
  setIsEditingInventory(false);
  return;
}

try {
  await updateLineItem.mutateAsync({ id: lineItemId, data: updateData });

  if (inventoryEditComment.trim()) {
    await handleCommentSubmit(inventoryEditComment, 'inventory_update');
  }

  queryClient.invalidateQueries({ queryKey: ['lineItemDetail', lineItemId] });
  setIsEditingInventory(false);
  setInventoryEditComment('');
} catch (error) {
  console.error('Failed to update inventory details:', error);
  alert('Failed to update inventory details. Please try again.');
}
};

const handleCancelEditInventory = () => {
setIsEditingInventory(false);
setInventoryEditComment('');
setInventoryValidationError(null);
};

// Rejected state editing handlers
const isRejected = data?.lineItem?.exceptionStatus === 'not_approved';
const canResubmit = isRejected && user && data?.holdRequest?.salesAeId === user.id;

const handleStartEditRejected = () => {
if (!data?.lineItem) return;
setRejectedEditData({
flightStartDate: isoToDisplayDate(data.lineItem.flightStartDate),
flightEndDate: isoToDisplayDate(data.lineItem.flightEndDate),
holdExpirationDate: isoToDisplayDate(data.lineItem.holdExpirationDate),
notes: '', // Empty field for sales user's reply
});
setIsEditingRejected(true);
};

const handleCancelEditRejected = () => {
setIsEditingRejected(false);
setRejectedEditData({});
};

const handleResubmit = async () => {
if (!data?.lineItem) return;
try {
await resubmitException.mutateAsync({
lineItemId,
updates: rejectedEditData,
});
setIsEditingRejected(false);
setRejectedEditData({});
queryClient.invalidateQueries({ queryKey: ['lineItemDetail', lineItemId] });
queryClient.invalidateQueries({ queryKey: ['searchLineItemComments', lineItemId] });
} catch (error) {
console.error('Failed to resubmit exception:', error);
alert('Failed to resubmit. Please try again.');
}
};

// Check if user can create comments on this line item
const canCreateComment = useCallback(() => {
if (!user || !data) return false;

const { lineItem, holdRequest } = data;
const userRole = user.role;

// Sales AE can comment on their own hold requests' line items
if (userRole === 'sales_ae' && holdRequest.salesAeId === user.id) {
return true;
}

// Service AE can comment on line items they're assigned to
if (userRole === 'service_ae' && lineItem.serviceAeId === user.id) {
return true;
}

// Charting team members can comment on line items assigned to them or in their queue
if (userRole === 'charting_member' || userRole === 'charting_manager') {
// Can comment if assigned to them
if (lineItem.assignedChartingUserId === user.id) {
return true;
}
// Can comment if it's not an exception hold (standard holds are in their queue)
if (!lineItem.isExceptionHold) {
return true;
}
}

// Exceptions team can comment on exception holds across all regions
if (userRole === 'exceptions_member' && lineItem.isExceptionHold) {
return true;
}

// Leadership roles can comment on any line item
if (
userRole === 'sales_vp' ||
userRole === 'gvp' ||
userRole === 'rvp' ||
userRole === 'adops_leadership'
) {
return true;
}

return false;
}, [user, data]);

// Check if user can view comments on this line item
const canViewComments = useCallback(() => {
if (!user || !data) return false;

const { lineItem, holdRequest } = data;
const userRole = user.role;

// Sales AE can view comments on their own hold requests' line items
if (userRole === 'sales_ae' && holdRequest.salesAeId === user.id) {
return true;
}

// Service AE can view comments on line items they're assigned to
if (userRole === 'service_ae' && lineItem.serviceAeId === user.id) {
return true;
}

// Charting team members can view comments on line items in their scope
if (userRole === 'charting_member' || userRole === 'charting_manager') {
return true;
}

// Exceptions team can view comments on exception holds
if (userRole === 'exceptions_member') {
return true;
}

// Leadership roles can view comments on any line item
if (
userRole === 'sales_vp' ||
userRole === 'gvp' ||
userRole === 'rvp' ||
userRole === 'adops_leadership' ||
userRole === 'admin'
) {
return true;
}

return false;
}, [user, data]);

// Handle comment submission
const handleCommentSubmit = async (text: string, actionContext?: ActionContext) => {
if (!lineItemId) return;

const commentData: CreateLineItemCommentData = {
lineItemId,
commentText: text,
};

// Only add actionContext if it's defined
if (actionContext) {
commentData.actionContext = actionContext;
}

await createComment.mutateAsync(commentData);
};

const getStatusBadgeColor = (status: string) => {
switch (status) {
case 'open':
return 'bg-outfront-purple-light text-outfront-purple';
case 'in_progress':
return 'bg-yellow-100 text-yellow-800';
case 'completed':
return 'bg-green-100 text-green-800';
case 'approval_required_by_exception':
return 'bg-purple-100 text-purple-800';
case 'revision_required_by_sales':
return 'bg-red-100 text-red-800';
case 'expired':
return 'bg-red-100 text-red-800';
// OPE-386 (c): 'cancelled' key used when workflowStatus='cancelled' is passed as status
case 'cancelled':
return 'bg-red-100 text-red-800';
default:
return 'bg-neutral-100 text-neutral-900';
}
};

const _getExpirationColor = (days: number) => {
if (days < 0) return 'text-red-600 font-semibold';
if (days === 0) return 'text-orange-600 font-semibold';
if (days <= 2) return 'text-yellow-600';
return 'text-neutral-600';
};

const formatDate = (dateString: string) => {
return new Date(dateString).toLocaleDateString('en-US', {
year: 'numeric',
month: 'short',
day: 'numeric',
timeZone: 'UTC',
});
};

const handleOpenActionModal = (action: 'complete' | 'exception') => {
setActionType(action);
setComments('');
if (action === 'complete' && data?.lineItem) {
setCompletionHoldPosition(data.lineItem.holdPosition || '');
}
setShowActionModal(true);
};

const handleSubmitAction = async () => {
if (!actionType) return;

if (actionType === 'complete' && onMarkCompleted) {
// Save hold position if changed before completing
if (completionHoldPosition && data?.lineItem && completionHoldPosition !== data.lineItem.holdPosition) {
try {
await updateHoldPosition.mutateAsync({
lineItemId,
holdPosition: completionHoldPosition as HoldPosition,
});
} catch (error) {
console.error('Failed to update hold position:', error);
// OPE-382: include the server's message. This path now calls `set-position`, which
// enforces the depth cap and the single-primary rule, so a rejection here is an
// ordinary business-rule outcome ("unit already has 3 holds (maximum 3)") rather than
// an unexpected failure — and "Please try again" is actively misleading advice for it,
// since retrying an over-cap unit fails identically. Kept as an `alert` to match this
// file's existing convention rather than introduce a second error surface here.
const msg = (error as { message?: string })?.message;
alert(msg ? `Hold position not changed: ${msg}` : 'Failed to update hold position. Please try again.');
return;
}
}
onMarkCompleted(lineItemId, comments || undefined);
} else if (actionType === 'exception' && onRequireException) {
if (!comments.trim()) {
alert('Reason is required for exception approval');
return;
}
onRequireException(lineItemId, comments);
}

setShowActionModal(false);
setComments('');
setCompletionHoldPosition('');
setActionType(null);
};

// Check if user can assign line items
const canAssignLineItem = useCallback(() => {
if (!user || !data) return false;

const userRole = user.role;

// Charting members and managers can assign line items
if (userRole === 'charting_member' || userRole === 'charting_manager') {
return true;
}

return false;
}, [user, data]);

// Check if user is a charting manager (can assign to others)
const isChartingManager = useCallback(() => {
return user?.role === 'charting_manager';
}, [user]);

// Handle assign to self
const handleAssignToSelf = async () => {
try {
await assignToSelf.mutateAsync(lineItemId);
queryClient.invalidateQueries({ queryKey: ['lineItemDetail', lineItemId] });
} catch (error) {
console.error('Failed to assign to self:', error);
alert('Failed to assign line item to yourself');
}
};

// Handle assign to team member
const handleAssignToTeam = async () => {
if (!selectedUserId) {
alert('Please select a team member');
return;
}

try {
await assignToUser.mutateAsync({ 
lineItemId, 
targetUserId: selectedUserId 
});
queryClient.invalidateQueries({ queryKey: ['lineItemDetail', lineItemId] });
setShowAssignModal(false);
setSelectedUserId('');
} catch (error) {
console.error('Failed to assign to team member:', error);
alert('Failed to assign line item to team member');
}
};

if (isLoading) {
const loadingContent = (
<div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto min-h-[600px]">
<LineItemDetailSkeleton />
</div>
);

return isModal ? (
<Modal isOpen={true} onClose={onClose} size="xl">
{loadingContent}
</Modal>
) : loadingContent;
}

if (error || !data || !data.lineItem || !data.holdRequest) {
console.error('LineItemDetail error:', { 
error, 
data: JSON.stringify(data, null, 2),
hasLineItem: !!data?.lineItem, 
hasHoldRequest: !!data?.holdRequest 
});

const errorMessage = error instanceof Error ? error.message : "Failed to load line item details";

const errorContent = (
<div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto min-h-[600px]">
<div className="flex items-center justify-between mb-4">
<h2 className="text-xl font-semibold">Line Item Details</h2>
<Button variant="secondary" onClick={onClose}>
Close
</Button>
</div>
<ErrorMessage message={errorMessage} />
</div>
);

return isModal ? (
<Modal isOpen={true} onClose={onClose} size="xl">
{errorContent}
</Modal>
) : errorContent;
}

const { lineItem, holdRequest } = data;

const content = (
<div className={isModal ? "bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto" : "bg-neutral-50 min-h-screen"}>
<div className={isModal ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
{/* Header with Actions */}
<div className="flex items-start justify-between mb-6">
<div className="flex-1">
<h1 className="text-3xl font-bold text-neutral-900 mb-2">{lineItem.displayName}</h1>
<div className="flex items-center gap-3 flex-wrap">
<StatusPill
status={lineItem.chartingStatus || 'open'}
disabled={!canEditLineItem()}
requestNumber={holdRequest.requestNumber}
lineItemName={lineItem.displayName}
/>
{lineItem.exceptionStatus && (
<Badge className={getStatusBadgeColor(lineItem.exceptionStatus)}>
Exception: {lineItem.exceptionStatus.replace('_', ' ').toUpperCase()}
</Badge>
)}
{lineItem.holdPosition && (
<HoldPositionBadge value={lineItem.holdPosition} />
)}
{lineItem.showingType && (
<Badge className="bg-purple-100 text-purple-800">
{showingTypes.find(s => s.code === lineItem.showingType)?.label || lineItem.showingType}
</Badge>
)}
</div>
</div>
<div className="flex gap-2">
{/* Navigation Arrows */}
{totalLineItems > 1 && (
<div className="flex items-center gap-1 mr-2 border-r pr-2">
<button
onClick={handlePrevious}
className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-neutral-100 transition-colors text-neutral-600 hover:text-neutral-900"
title="Previous line item (←)"
aria-label="Previous line item"
>
<svg 
xmlns="http://www.w3.org/2000/svg" 
className="h-5 w-5" 
fill="none" 
viewBox="0 0 24 24" 
stroke="currentColor"
>
<path 
strokeLinecap="round" 
strokeLinejoin="round" 
strokeWidth={2} 
d="M15 19l-7-7 7-7" 
/>
</svg>
</button>
<span className="text-sm text-neutral-600 font-medium min-w-[3rem] text-center">
{currentIndex + 1} / {totalLineItems}
</span>
<button
onClick={handleNext}
className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-neutral-100 transition-colors text-neutral-600 hover:text-neutral-900"
title="Next line item (→)"
aria-label="Next line item"
>
<svg 
xmlns="http://www.w3.org/2000/svg" 
className="h-5 w-5" 
fill="none" 
viewBox="0 0 24 24" 
stroke="currentColor"
>
<path 
strokeLinecap="round" 
strokeLinejoin="round" 
strokeWidth={2} 
d="M9 5l7 7-7 7" 
/>
</svg>
</button>
</div>
)}
{isModal && (
<>
<button
onClick={handleCopyLink}
className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-neutral-100 transition-colors text-neutral-600 hover:text-neutral-900"
title={linkCopied ? 'Link copied!' : 'Copy link to line item'}
aria-label="Copy link to line item"
>
{linkCopied ? (
<svg
xmlns="http://www.w3.org/2000/svg"
className="h-5 w-5 text-green-600"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M5 13l4 4L19 7"
/>
</svg>
) : (
<svg
xmlns="http://www.w3.org/2000/svg"
className="h-5 w-5"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
/>
</svg>
)}
</button>
<button
onClick={() => navigate(`/items/${lineItemId}`)}
className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-neutral-100 transition-colors text-neutral-600 hover:text-neutral-900"
title="Open in full page"
aria-label="Open in full page"
>
<svg 
xmlns="http://www.w3.org/2000/svg" 
className="h-5 w-5" 
fill="none" 
viewBox="0 0 24 24" 
stroke="currentColor"
>
<path 
strokeLinecap="round" 
strokeLinejoin="round" 
strokeWidth={2} 
d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
/>
</svg>
</button>
<button
onClick={onClose}
className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-neutral-100 transition-colors text-neutral-600 hover:text-neutral-900"
title="Close"
aria-label="Close"
>
<svg 
xmlns="http://www.w3.org/2000/svg" 
className="h-5 w-5" 
fill="none" 
viewBox="0 0 24 24" 
stroke="currentColor"
>
<path 
strokeLinecap="round" 
strokeLinejoin="round" 
strokeWidth={2} 
d="M6 18L18 6M6 6l12 12" 
/>
</svg>
</button>
</>
)}
</div>
</div>

{/* Rejected Banner */}
{isRejected && (
<div className="mb-6 bg-red-50 border border-red-300 rounded-lg p-4">
<div className="flex items-center justify-between">
<div className="flex items-center gap-2">
<svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
</svg>
<span className="text-sm font-semibold text-red-800">This line item was rejected.</span>
</div>
{canResubmit && !isEditingRejected && (
<button
onClick={handleStartEditRejected}
className="flex items-center gap-1 text-sm text-red-700 hover:text-red-900 font-medium"
>
<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
</svg>
Edit &amp; Resubmit
</button>
)}
</div>

{/* Show latest exception comment from admin */}
{latestExceptionComment && (
<div className="mt-3 p-3 bg-white border border-red-200 rounded-md">
<div className="flex items-center gap-2 mb-1">
<span className="text-xs font-semibold text-neutral-600">
{latestExceptionComment.authorName}
</span>
<span className="text-xs text-neutral-400">
{new Date(latestExceptionComment.createdAt).toLocaleString('en-US', {
month: 'short',
day: 'numeric',
hour: 'numeric',
minute: '2-digit',
})}
</span>
</div>
<p className="text-sm text-neutral-900">{latestExceptionComment.commentText}</p>
</div>
)}

{isEditingRejected && (
<div className="mt-4 space-y-3 border-t border-red-200 pt-4">
<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
<div>
<label className="block text-xs font-semibold text-neutral-600 mb-1">Flight Start</label>
<input
type="date"
className="w-full text-sm rounded border border-neutral-200 px-2 py-1.5"
value={rejectedEditData.flightStartDate || ''}
onChange={(e) => setRejectedEditData(prev => ({ ...prev, flightStartDate: e.target.value }))}
/>
</div>
<div>
<label className="block text-xs font-semibold text-neutral-600 mb-1">Flight End</label>
<input
type="date"
className="w-full text-sm rounded border border-neutral-200 px-2 py-1.5"
value={rejectedEditData.flightEndDate || ''}
onChange={(e) => setRejectedEditData(prev => ({ ...prev, flightEndDate: e.target.value }))}
/>
</div>
<div>
<label className="block text-xs font-semibold text-neutral-600 mb-1">Hold Expiration</label>
<input
type="date"
className="w-full text-sm rounded border border-neutral-200 px-2 py-1.5"
value={rejectedEditData.holdExpirationDate || ''}
onChange={(e) => setRejectedEditData(prev => ({ ...prev, holdExpirationDate: e.target.value }))}
/>
</div>
</div>
<div>
<label className="block text-xs font-semibold text-neutral-600 mb-1">Your Reply</label>
<textarea
className="w-full text-sm rounded border border-neutral-200 px-2 py-1.5"
rows={2}
value={rejectedEditData.notes || ''}
onChange={(e) => setRejectedEditData(prev => ({ ...prev, notes: e.target.value }))}
placeholder="Add your response to the rejection..."
/>
</div>
<div className="flex gap-2">
<Button
size="sm"
onClick={handleResubmit}
disabled={resubmitException.isPending}
>
{resubmitException.isPending ? 'Resubmitting...' : 'Resubmit'}
</Button>
<Button
size="sm"
variant="secondary"
onClick={handleCancelEditRejected}
disabled={resubmitException.isPending}
>
Cancel
</Button>
</div>
</div>
)}
</div>
)}

{/* Hold Request Callout */}
<div className="mb-6 bg-outfront-purple-light border border-outfront-purple rounded-lg p-4">
<div className="flex items-start justify-between">
<div className="flex-1">
<div className="flex items-center gap-2 mb-2">
<span className="text-sm font-medium text-outfront-purple">Part of Hold Request</span>
<button
onClick={() => navigate(`/holds/${holdRequest.id}`)}
className="text-sm font-semibold text-outfront-purple hover:text-outfront-purple hover:underline transition-colors"
>
#{holdRequest.requestNumber}
</button>
{/* OPE-386 (c): workflowStatus='cancelled' overrides derivedStatus — pass 'cancelled' key to getStatusBadgeColor */}
{(holdRequest.workflowStatus === 'cancelled' || holdRequest.derivedStatus) && (
<Badge className={getStatusBadgeColor(holdRequest.workflowStatus === 'cancelled' ? 'cancelled' : holdRequest.derivedStatus)}>
{holdRequest.workflowStatus === 'cancelled' ? 'CANCELLED' : holdRequest.derivedStatus.replace(/_/g, ' ').toUpperCase()}
</Badge>
)}
</div>
<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
<div>
<span className="text-outfront-purple font-medium">Advertiser:</span>
<span className="ml-1 text-outfront-purple">{holdRequest.advertiser}</span>
</div>
<div>
<span className="text-outfront-purple font-medium">Category:</span>
<span className="ml-1 text-outfront-purple capitalize">
{holdRequest.businessCategory.replace('_', ' ')}
</span>
</div>
<div>
<span className="text-outfront-purple font-medium">Sales AE:</span>
<span className="ml-1 text-outfront-purple">
{holdRequest.salesAe ? 
`${holdRequest.salesAe.firstName} ${holdRequest.salesAe.lastName}` : 
'Not assigned'
}
</span>
</div>
{holdRequest.isCrossRegion && (
<div>
<Badge className="bg-outfront-purple-light text-outfront-purple">Cross-Region</Badge>
</div>
)}
{holdRequest.isAutoPerm && (
<div>
<Badge className="bg-outfront-purple-light text-outfront-purple">Auto-Perm</Badge>
</div>
)}
</div>
</div>
</div>
</div>

{/* Main Content Grid */}
<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
{/* Left Column - Inventory Details (2 columns wide) */}
<div className="lg:col-span-2 space-y-6">
{/* Inventory Details */}
<Card>
<CardHeader>
<div className="flex items-center justify-between">
<CardTitle>Inventory Details</CardTitle>
{canEditLineItem() && !isEditingInventory && (
<Button
size="sm"
variant="ghost"
onClick={handleStartEditInventory}
className="text-xs"
>
Edit Details
</Button>
)}
</div>
</CardHeader>
<CardContent>
<div className="grid grid-cols-2 gap-6">
{/* Row 1: Market + Media Type */}
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Market</label>
<p className="text-lg font-semibold text-neutral-900 mt-1">{lineItem.market}</p>
</div>
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Media Type</label>
<p className="text-lg font-semibold text-neutral-900 mt-1 capitalize">{lineItem.mediaType}</p>
</div>

{/* Row 2: Qty + # of Spots */}
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Qty</label>
{isEditingInventory ? (
<input
type="number"
min={1}
step={1}
value={editedInventory.qty}
onChange={(e) => setEditedInventory(prev => ({ ...prev, qty: e.target.value === '' ? '' as unknown as number : Number(e.target.value) }))}
className="w-full px-2 py-1 text-base font-medium text-neutral-900 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple mt-1"
/>
) : (
<p className="text-lg font-semibold text-neutral-900 mt-1">{lineItem.qty ?? 1}</p>
)}
</div>
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide"># of Spots</label>
{isEditingInventory ? (
<input
type="number"
min={0}
step={1}
value={editedInventory.digitalOnlyNoOfSpots}
onChange={(e) => setEditedInventory(prev => ({ ...prev, digitalOnlyNoOfSpots: e.target.value === '' ? '' : Number(e.target.value) }))}
placeholder="—"
className="w-full px-2 py-1 text-base font-medium text-neutral-900 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple mt-1"
/>
) : (
<p className="text-lg font-semibold text-neutral-900 mt-1">
{lineItem.digitalOnlyNoOfSpots != null ? lineItem.digitalOnlyNoOfSpots : '—'}
</p>
)}
</div>

{/* Row 3: Unit ID / Coverage + Negotiated Rate */}
{lineItem.unitProductId ? (
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Unit ID / Product ID</label>
<p className="text-base font-mono text-neutral-900 mt-1 bg-neutral-50 px-3 py-2 rounded border border-neutral-200">
{lineItem.unitProductId}
</p>
</div>
) : lineItem.coverage ? (
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Coverage</label>
<p className="text-base text-neutral-900 mt-1 bg-neutral-50 px-3 py-2 rounded border border-neutral-200">
{lineItem.coverage}
</p>
</div>
) : (
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Unit ID / Coverage</label>
<p className="text-base text-neutral-400 italic mt-1 bg-neutral-50 px-3 py-2 rounded border border-neutral-200">
Not specified
</p>
</div>
)}
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Negotiated Per Period Rate</label>
<p className="text-base font-medium text-neutral-900 mt-1">
{lineItem.negotiatedPerPeriodRate != null
? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(lineItem.negotiatedPerPeriodRate)
: '—'}
</p>
</div>

{/* Showing Type */}
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Showing Type</label>
<p className="text-lg font-semibold text-neutral-900 mt-1">
  {showingTypes.find(s => s.code === lineItem.showingType)?.label || lineItem.showingType || '—'}
</p>
</div>

{/* Service AE (if applicable) */}
{lineItem.serviceAe && (
<div className="col-span-2">
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Service AE</label>
<div className="mt-1 flex items-center gap-2">
<p className="text-base font-medium text-neutral-900">
{`${lineItem.serviceAe.firstName} ${lineItem.serviceAe.lastName}`}
</p>
<span className="text-sm text-neutral-600">({lineItem.serviceAe.email})</span>
{lineItem.serviceAe.primaryRegion && (
<Badge className="bg-neutral-100 text-neutral-600 text-xs">
{lineItem.serviceAe.primaryRegion}
</Badge>
)}
</div>
</div>
)}
</div>

{/* Validation Error */}
{isEditingInventory && inventoryValidationError && (
<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
<p className="text-xs text-red-800 font-semibold">{inventoryValidationError}</p>
</div>
)}

{/* Edit Actions */}
{isEditingInventory && (
<div className="mt-4 space-y-3 pt-4 border-t">
<div>
<label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-1">
Comment (optional)
</label>
<textarea
className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple text-sm"
rows={2}
value={inventoryEditComment}
onChange={(e) => setInventoryEditComment(e.target.value)}
placeholder="Add a note about this change..."
/>
</div>
<div className="flex gap-2">
<Button
size="sm"
onClick={handleSaveInventory}
disabled={!!inventoryValidationError || updateLineItem.isPending}
className="flex-1"
>
{updateLineItem.isPending ? 'Saving...' : 'Save Changes'}
</Button>
<Button
size="sm"
variant="secondary"
onClick={handleCancelEditInventory}
disabled={updateLineItem.isPending}
className="flex-1"
>
Cancel
</Button>
</div>
</div>
)}
</CardContent>
</Card>

{/* Dates Section - Combined */}
<Card>
<CardHeader>
<div className="flex items-center justify-between">
<CardTitle>Line Item Dates</CardTitle>
{canEditLineItem() && !isEditingDates && (
<Button
size="sm"
variant="ghost"
onClick={handleStartEditDates}
className="text-xs"
>
Edit Dates
</Button>
)}
</div>
</CardHeader>
<CardContent>
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
{/* Hold Dates Column */}
<div className="space-y-4">
<h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide border-b pb-2">Hold Dates</h4>
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Hold Start</label>
<p className="text-sm font-medium text-neutral-900 mt-1">{holdRequest.holdStartDate ? formatDate(holdRequest.holdStartDate) : 'N/A'}</p>
</div>
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Hold Expiration</label>
{isEditingDates ? (
<DatePicker
value={editedDates.holdExpirationDate}
onChange={(val) => setEditedDates({ ...editedDates, holdExpirationDate: val })}
className="mt-1"
minDate={new Date(0)}
defaultToToday={false}
/>
) : (
(() => {
const daysUntilExpiration = lineItem.holdExpirationDate
? Math.ceil((new Date(lineItem.holdExpirationDate).getTime() - new Date().setHours(0,0,0,0)) / (1000 * 60 * 60 * 24))
: undefined;
return (
<>
<p className="text-sm font-medium text-neutral-900 mt-1">{lineItem.holdExpirationDate ? formatDate(lineItem.holdExpirationDate) : 'N/A'}</p>
{daysUntilExpiration !== undefined && (
<div className={`mt-2 px-3 py-1.5 rounded-md inline-block text-xs font-semibold ${
daysUntilExpiration < 0 
? 'bg-red-100 text-red-800' 
: daysUntilExpiration === 0 
? 'bg-orange-100 text-orange-800'
: daysUntilExpiration <= 2
? 'bg-yellow-100 text-yellow-800'
: 'bg-green-100 text-green-800'
}`}>
{daysUntilExpiration < 0
? `${Math.abs(daysUntilExpiration)} days overdue`
: daysUntilExpiration === 0
? 'Expires today'
: `${daysUntilExpiration} days remaining`}
</div>
)}
</>
);
})()
)}
</div>
</div>

{/* Flight Dates Column */}
<div className="space-y-4">
<h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide border-b pb-2">Campaign Flight Dates</h4>
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Flight Start</label>
{isEditingDates ? (
<DatePicker
value={editedDates.flightStartDate}
onChange={(val) => setEditedDates({ ...editedDates, flightStartDate: val })}
className="mt-1"
/>
) : (
<p className="text-sm font-medium text-neutral-900 mt-1">{formatDate(lineItem.flightStartDate)}</p>
)}
</div>
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Flight End</label>
{isEditingDates ? (
<DatePicker
value={editedDates.flightEndDate}
onChange={(val) => setEditedDates({ ...editedDates, flightEndDate: val })}
className="mt-1"
/>
) : (
<p className="text-sm font-medium text-neutral-900 mt-1">{formatDate(lineItem.flightEndDate)}</p>
)}
</div>
</div>
</div>

{/* Movie Studio Gap Validation */}
{holdRequest.businessCategory === 'movie_studio' && !isEditingDates && (
<div className="mt-4 p-3 bg-outfront-purple-light border border-outfront-purple rounded-md">
<p className="text-xs text-outfront-purple font-semibold">
Movie Studio Gap Requirement
</p>
<p className="text-xs text-outfront-purple mt-1">
Minimum 60 business days required between hold expiration and flight start
</p>
</div>
)}

{/* Validation Error */}
{isEditingDates && dateValidationError && (
<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
<p className="text-xs text-red-800 font-semibold">
{dateValidationError}
</p>
</div>
)}

{/* Edit Actions */}
{isEditingDates && (
<div className="mt-4 space-y-3 pt-4 border-t">
<div>
<label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-1">
Comment (optional)
</label>
<textarea
className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple text-sm"
rows={2}
value={editComment}
onChange={(e) => setEditComment(e.target.value)}
placeholder="Add a note about this change..."
/>
</div>
<div className="flex gap-2">
<Button
size="sm"
onClick={handleSaveDates}
disabled={!!dateValidationError || updateLineItem.isPending}
className="flex-1"
>
{updateLineItem.isPending ? 'Saving...' : 'Save Changes'}
</Button>
<Button
size="sm"
variant="secondary"
onClick={handleCancelEditDates}
disabled={updateLineItem.isPending}
className="flex-1"
>
Cancel
</Button>
</div>
</div>
)}
</CardContent>
</Card>
</div>

{/* Right Column - Status & Assignment */}
<div className="space-y-6">
{/* Assignment */}
<Card>
<CardHeader>
<CardTitle className="text-base">Assignment</CardTitle>
</CardHeader>
<CardContent>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Assigned To</label>
<p className="text-sm font-medium text-neutral-900 mt-2">
{lineItem.assignedChartingUser ? 
`${lineItem.assignedChartingUser.firstName} ${lineItem.assignedChartingUser.lastName}` : 
<span className="text-neutral-400 italic">Unassigned</span>
}
</p>
</CardContent>
</Card>

{/* Hold Position */}
<Card>
<CardHeader>
<CardTitle className="text-base">Hold Position</CardTitle>
</CardHeader>
<CardContent>
{canEditHoldPosition() ? (
<HoldPositionDropdown
lineItemId={lineItem.id}
currentValue={lineItem.holdPosition}
chartingStatus={lineItem.chartingStatus}
/>
) : (
<div>
<label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Position</label>
<div className="mt-2">
<HoldPositionBadge value={lineItem.holdPosition} />
</div>
</div>
)}
</CardContent>
</Card>

{/* Actions */}
{(canComplete || canAssignLineItem()) && (
<Card>
<CardHeader>
<CardTitle className="text-base">Actions</CardTitle>
</CardHeader>
<CardContent className="space-y-2">
{/* Assignment Actions */}
{canAssignLineItem() && (
<>
<Button 
onClick={handleAssignToSelf}
className="w-full"
disabled={assignToSelf.isPending || lineItem.assignedChartingUserId === user?.id}
>
{assignToSelf.isPending ? 'Assigning...' : lineItem.assignedChartingUserId === user?.id ? 'Assigned to Me' : 'Assign to Me'}
</Button>

{isChartingManager() && (
<Button 
onClick={() => setShowAssignModal(true)}
variant="secondary"
className="w-full"
>
Assign to Team
</Button>
)}
</>
)}

{/* Post-assignment Actions (only show when assigned to current user) */}
{canComplete && isAssignedToUser && (
<Button
onClick={() => handleOpenActionModal('complete')}
className="w-full"
disabled={lineItem.isExceptionHold && lineItem.exceptionStatus !== 'approved'}
>
Mark Complete
</Button>
)}
{canComplete && isAssignedToUser && (
<Button
variant="secondary"
onClick={() => handleOpenActionModal('exception')}
className="w-full"
disabled={lineItem.chartingStatus === 'approval_required_by_exception' || lineItem.exceptionStatus === 'in_review'}
>
{lineItem.chartingStatus === 'approval_required_by_exception' || lineItem.exceptionStatus === 'in_review'
? 'Exception Pending'
: 'Need Exception'}
</Button>
)}
</CardContent>
</Card>
)}
</div>
</div>

{/* Comments Section */}
{canViewComments() && (
<Card className="mb-6">
<CardHeader>
<div className="flex items-center justify-between">
<CardTitle>Comments & Activity</CardTitle>
<Button
variant="ghost"
onClick={() => setShowComments(!showComments)}
className="text-sm"
>
{showComments ? 'Hide' : 'Show'}
</Button>
</div>
</CardHeader>

{showComments && (
<CardContent className="space-y-6">
{/* Comment Input - Only show if user has permission */}
{canCreateComment() && (
<div className="pb-6 border-b border-neutral-200">
<CommentInput
onSubmit={handleCommentSubmit}
placeholder="Add a comment to this line item..."
disabled={createComment.isPending}
/>
</div>
)}

{/* Comment History */}
<div>
<PaginatedCommentHistory
lineItemId={lineItemId}
pageSize={20}
enableCollapsible={true}
initialCollapsedCount={5}
recentThresholdMinutes={5}
/>
</div>
</CardContent>
)}
</Card>
)}

{/* Audit Trail Section */}
<Card className="mb-6">
<CardHeader>
<div className="flex items-center justify-between">
<CardTitle>Audit Trail</CardTitle>
<Button
variant="ghost"
onClick={() => setShowAuditTrail(!showAuditTrail)}
className="text-sm"
>
{showAuditTrail ? 'Hide' : 'Show'}
</Button>
</div>
</CardHeader>

{showAuditTrail && (
<CardContent>
<AuditViewer
tableName="inventory_line_items"
recordId={lineItemId}
title="Line Item Audit Trail"
/>
</CardContent>
)}
</Card>
</div>
</div>
);

return (
<>
{isModal ? (
<Modal isOpen={true} onClose={onClose} size="xl">
{content}
</Modal>
) : (
content
)}

{/* Action Modal */}
<Modal isOpen={showActionModal} onClose={() => { setShowActionModal(false); setComments(''); setCompletionHoldPosition(''); setActionType(null); }} layer="above">
<div className="bg-white rounded-lg p-6">
<h3 className="text-lg font-semibold mb-4">
{actionType === 'complete' ? 'Mark as Completed' : 'Require Exception Approval'}
</h3>
{actionType === 'complete' && (
<div className="mb-4">
<label className="block text-sm font-medium text-neutral-600 mb-2">
Hold Position <span className="text-red-500">*</span>
</label>
<select
className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple"
value={completionHoldPosition}
onChange={(e) => setCompletionHoldPosition(e.target.value)}
>
<option key="empty" value="" disabled>Select position</option>
<option key="primary" value="primary">Primary</option>
<option key="secondary" value="secondary">Secondary</option>
<option key="tertiary" value="tertiary">Tertiary</option>
</select>
{!completionHoldPosition && (
<p className="text-xs text-red-600 mt-1">Hold position is required before marking as completed</p>
)}
</div>
)}
<div className="mb-4">
<label className="block text-sm font-medium text-neutral-600 mb-2">
{actionType === 'complete' ? 'Comments (optional)' : 'Reason (required)'}
</label>
<textarea
className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple"
rows={4}
value={comments}
onChange={(e) => setComments(e.target.value)}
placeholder={
actionType === 'complete'
? 'Add any notes about this completion...'
: 'Explain why exception approval is needed...'
}
/>
</div>
<div className="flex justify-end gap-2">
<Button
variant="secondary"
onClick={() => {
setShowActionModal(false);
setComments('');
setCompletionHoldPosition('');
setActionType(null);
}}
>
Cancel
</Button>
<Button
onClick={handleSubmitAction}
disabled={actionType === 'complete' && !completionHoldPosition}
>
{actionType === 'complete' ? 'Mark Complete' : 'Send for Approval'}
</Button>
</div>
</div>
</Modal>

{/* Assign to Team Modal */}
<Modal isOpen={showAssignModal} onClose={() => { setShowAssignModal(false); setSelectedUserId(''); }} layer="above">
<div className="bg-white rounded-lg p-6">
<h3 className="text-lg font-semibold mb-4">Assign to Team Member</h3>
<div className="mb-4">
<label className="block text-sm font-medium text-neutral-600 mb-2">
Select Team Member
</label>
<select
className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple"
value={selectedUserId}
onChange={(e) => setSelectedUserId(e.target.value)}
>
<option key="empty" value="">-- Select a team member --</option>
{teamMembers?.map((member: User) => (
<option key={member.id} value={member.id}>
{member.firstName} {member.lastName} ({(member.role ?? '').replace('_', ' ')})
</option>
))}
</select>
</div>
<div className="flex justify-end gap-2">
<Button
variant="secondary"
onClick={() => {
setShowAssignModal(false);
setSelectedUserId('');
}}
>
Cancel
</Button>
<Button 
onClick={handleAssignToTeam}
disabled={!selectedUserId || assignToUser.isPending}
>
{assignToUser.isPending ? 'Assigning...' : 'Assign'}
</Button>
</div>
</div>
</Modal>
</>
);
}